/**
 * Google Analytics 4 integration with custom event tracking.
 *
 * Setup:
 * 1. Add VITE_GA_MEASUREMENT_ID to your .env file (e.g., G-XXXXXXXXXX)
 * 2. Call initializeAnalytics() in your app entry point
 * 3. Use tracking functions throughout your app
 */

import { isAnalyticsAllowed } from "./cookie-consent";

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

let isInitialized = false;
// Whether gtag('config') has run at least once this page load. Guards against
// re-sending an initial page_view when analytics is re-enabled after an opt-out.
let isConfigured = false;

/**
 * Initialize Google Analytics 4.
 * Call this once in your app entry point (e.g., main.tsx or App.tsx).
 */
export const initializeAnalytics = (): void => {
  if (isInitialized || !GA_MEASUREMENT_ID) {
    if (!GA_MEASUREMENT_ID) {
      console.warn('Google Analytics: VITE_GA_MEASUREMENT_ID not set');
    }
    return;
  }

  // Respect the visitor's consent choice (and the GPC signal).
  if (!isAnalyticsAllowed()) return;

  // Clear any prior opt-out flag so re-enabling after disableAnalytics() takes effect.
  (window as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  // Dynamically inject the gtag script (consent-gated), avoiding duplicates.
  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = scriptSrc;
    document.head.appendChild(script);
  }

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  // Only configure (and send the initial page_view) once per page load. On a
  // re-enable after opt-out the tag is already configured; flipping the
  // ga-disable flag above is enough to resume tracking.
  if (!isConfigured) {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: true,
    });
    isConfigured = true;
  }

  isInitialized = true;
};

/**
 * Expire GA cookies (`_ga`, `_ga_*`, `_gid`) across the current host and its
 * parent/registrable domains. GA sets these on the eTLD+1 (e.g. `.example.com`)
 * by default, so a delete scoped only to the full hostname would miss them.
 */
const clearGaCookies = (): void => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const domains: (string | undefined)[] = [undefined, hostname];
  for (let i = 0; i < parts.length - 1; i++) {
    const candidate = parts.slice(i).join('.');
    // Skip single-label suffixes like ".com" to avoid overly-broad deletes.
    if (candidate.split('.').length >= 2) {
      domains.push(`.${candidate}`);
    }
  }

  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0]?.trim();
    if (name && /^_ga|^_gid/.test(name)) {
      domains.forEach((domain) => {
        document.cookie =
          `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/` +
          (domain ? `;domain=${domain}` : '');
      });
    }
  });
};

/**
 * Disable Google Analytics and clear its cookies.
 * Sets the gtag opt-out flag, resets initialization state, and deletes GA
 * cookies across the current host and its parent/registrable domains.
 */
export const disableAnalytics = (): void => {
  isInitialized = false;

  // GA opt-out flag (only meaningful when a measurement ID is configured).
  if (GA_MEASUREMENT_ID) {
    (window as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  }

  // Always clear GA cookies, even if the measurement ID has since been removed.
  clearGaCookies();
};

/**
 * Track a custom event in Google Analytics.
 */
const trackEvent = (
  eventName: string,
  eventParams?: Record<string, string | number | boolean>
): void => {
  if (!isInitialized || !window.gtag) return;
  window.gtag('event', eventName, eventParams);
};

// ============================================================================
// Coupon Events
// ============================================================================

export interface CouponEventParams {
  couponId: string;
  couponTitle: string;
  couponValue?: string;
  couponCategory?: string;
}

/**
 * Track when a user clips/saves a coupon.
 */
export const trackCouponClip = (params: CouponEventParams): void => {
  trackEvent('coupon_clip', {
    coupon_id: params.couponId,
    coupon_title: params.couponTitle,
    coupon_value: params.couponValue || '',
    coupon_category: params.couponCategory || '',
  });
};

/**
 * Track when a user views coupon details.
 */
export const trackCouponView = (params: CouponEventParams): void => {
  trackEvent('coupon_view', {
    coupon_id: params.couponId,
    coupon_title: params.couponTitle,
    coupon_value: params.couponValue || '',
    coupon_category: params.couponCategory || '',
  });
};

/**
 * Track when a user unclips/removes a coupon.
 */
export const trackCouponUnclip = (params: CouponEventParams): void => {
  trackEvent('coupon_unclip', {
    coupon_id: params.couponId,
    coupon_title: params.couponTitle,
  });
};

// ============================================================================
// Ad Events
// ============================================================================

export interface AdEventParams {
  adId?: string | number;
  adTitle?: string;
  adDate?: string;
  pageNumber?: number;
}

/**
 * Track when a user views a weekly ad.
 */
export const trackAdView = (params: AdEventParams): void => {
  trackEvent('ad_view', {
    ad_id: String(params.adId || ''),
    ad_title: params.adTitle || '',
    ad_date: params.adDate || '',
  });
};

/**
 * Track ad page navigation.
 */
export const trackAdPageView = (params: AdEventParams): void => {
  trackEvent('ad_page_view', {
    ad_id: String(params.adId || ''),
    page_number: params.pageNumber || 1,
  });
};

// ============================================================================
// Recipe Events
// ============================================================================

export interface RecipeEventParams {
  recipeId: number;
  recipeName: string;
  recipeCuisine?: string;
  recipeType?: string;
}

/**
 * Track when a user views a recipe.
 */
export const trackRecipeView = (params: RecipeEventParams): void => {
  trackEvent('recipe_view', {
    recipe_id: params.recipeId,
    recipe_name: params.recipeName,
    recipe_cuisine: params.recipeCuisine || '',
    recipe_type: params.recipeType || '',
  });
};

/**
 * Track when a user prints a recipe.
 */
export const trackRecipePrint = (params: RecipeEventParams): void => {
  trackEvent('recipe_print', {
    recipe_id: params.recipeId,
    recipe_name: params.recipeName,
  });
};

/**
 * Track when a user shares a recipe.
 */
export const trackRecipeShare = (params: RecipeEventParams & { method: string }): void => {
  trackEvent('recipe_share', {
    recipe_id: params.recipeId,
    recipe_name: params.recipeName,
    share_method: params.method,
  });
};

// ============================================================================
// Search Events
// ============================================================================

export interface SearchEventParams {
  searchTerm: string;
  searchCategory?: string;
  resultsCount?: number;
}

/**
 * Track search queries.
 */
export const trackSearch = (params: SearchEventParams): void => {
  trackEvent('search', {
    search_term: params.searchTerm,
    search_category: params.searchCategory || 'all',
    results_count: params.resultsCount ?? -1,
  });
};

/**
 * Track when a user clicks a search result.
 */
export const trackSearchResultClick = (params: {
  searchTerm: string;
  resultType: string;
  resultId: string;
  resultTitle: string;
  position: number;
}): void => {
  trackEvent('search_result_click', {
    search_term: params.searchTerm,
    result_type: params.resultType,
    result_id: params.resultId,
    result_title: params.resultTitle,
    position: params.position,
  });
};

// ============================================================================
// Store Events
// ============================================================================

export interface StoreEventParams {
  storeId: number;
  storeName: string;
  storeCity?: string;
  storeState?: string;
}

/**
 * Track when a user selects/changes their store.
 */
export const trackStoreSelect = (params: StoreEventParams): void => {
  trackEvent('store_select', {
    store_id: params.storeId,
    store_name: params.storeName,
    store_city: params.storeCity || '',
    store_state: params.storeState || '',
  });
};

/**
 * Track when a user views store details.
 */
export const trackStoreView = (params: StoreEventParams): void => {
  trackEvent('store_view', {
    store_id: params.storeId,
    store_name: params.storeName,
  });
};

// ============================================================================
// Contact Events
// ============================================================================

/**
 * Track contact form submission.
 */
export const trackContactSubmit = (params: { subject?: string }): void => {
  trackEvent('contact_submit', {
    subject: params.subject || '',
  });
};

// ============================================================================
// Page View (for SPA navigation)
// ============================================================================

/**
 * Track page views for SPA navigation.
 * Use this when route changes if automatic page tracking isn't sufficient.
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  if (!isInitialized || !window.gtag) return;
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle,
  });
};
